import React, { ReactElement, useCallback, useEffect, useState } from "react";
import ReactPlayer, { Config } from "react-player/lazy";
import { useLongPress } from "use-long-press";

export interface VideoProps {
  url: string;
  className?: string;
  height?: string;
  width?: string;
  isBackground?: boolean;
  volume?: number;
  poster?: ReactElement;
  children?: ReactElement;
  autoplay?: boolean;
  controls?: boolean;
  playing?: boolean;
  loop?: boolean;
  muted?: boolean;
}

/**
 * Video Component
 * takes a url (vimeo, youtube, twitter, etc.) and displays it with react-player
 * For now we only expect and support Vimeo. Support for other platforms might require updating the configs.
 * @param autoplay
 * tells video if it should autoplay
 * @param children
 * pass children into the video to display as an overlay (react-player playicon)
 * @param url
 * pass url into the video to or it won't work
 * @returns
 */
const Video: React.FC<VideoProps> = ({
  url,
  className,
  height,
  width,
  isBackground,
  volume,
  poster,
  children,
  autoplay,
  controls,
  playing,
  loop,
  muted
}: VideoProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(playing);
  const [hasInitialized, setHasInitialized] = useState(false);

  // We need this, so that the content below, and the <ReactPlayer /> doesn't get rendered, until the Video component itself gets rendered initially.
  // Without this, we end up with "Minified React Errors" in the console, and the background video hero twitches/loads twice. Our theory is that the 
  // video component is built in the static page, then after hydration, it doesn't match, and thus causes the errors and twitching effect.
  useEffect(() => {
    setHasInitialized(true);
  }, [hasInitialized]);

  // currently we only support Vimeo. To support other platforms, this config might need to be updated.
  const videoConfig: Config = {
    vimeo: {
      playerOptions: {
        background: isBackground,
        playsinline: true,
        loop: loop,
        volume: 0,
        muted: muted
      }
    },
    youtube: {
      playerVars: { showinfo: 1 }
    }
  };

  const longPressHandler = useLongPress(
    () => {
      // pause or resume video if user presses and holds for 1 sec
      if (isBackground) {
        setIsVideoPlaying(!isVideoPlaying);
      }
    },
    { threshold: 1000 }
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (isBackground && e.keyCode === 32) {
        e.preventDefault();
        setIsVideoPlaying(!isVideoPlaying);
      }
    },
    [isVideoPlaying, isBackground]
  );

  // Long press also triggers the context menu on some mobile devices
  const handleContextMenu = (event: React.MouseEvent): void => {
    if (isBackground) {
      event.preventDefault();
    }
  };

  return (
    <>
      {hasInitialized && (
        <div
          className={className}
          tabIndex={isBackground ? 0 : undefined}
          data-testid="video-wrapper"
          onKeyDown={(e) => handleKeyDown(e)}
          onContextMenu={(e) => handleContextMenu(e)}
          {...longPressHandler()}
        >
          <ReactPlayer
            autoPlay={autoplay}
            controls={controls}
            height={height}
            playing={isVideoPlaying}
            playsinline={true}
            width={width}
            url={url}
            playIcon={children}
            light={false}
            muted={muted}
            volume={volume}
            loop={loop}
            poster={poster}
            config={videoConfig}
            style={isBackground ? { pointerEvents: "none" } : undefined}
          />
        </div>
      )}
    </>
  );
};

export default Video;
